import { ActivatedRoute, Params, Router } from '@angular/router';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FilterFunctions } from 'src/app/pipes.module/_filter.functions';
import { TableCellDescriptor } from 'src/app/model.module/table-cell.model';
import {
    BuergschaftenListComponent
} from 'src/app/insight.component.module/insight-shared/components/bgsch-list/bgsch-list.component';
import { AppConstants } from 'src/app/app-constants.service';
import { AppState } from 'src/app/app.state';
import { SuperComponent } from '../../../inside.super';
import { RouteNames } from '../../../../route.name';
import { BuergscheinLight, VertragData } from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { ConfigurationApiService } from '../../../../../generated/api/configuration/configuration.service';
import { VertraegeService } from '../../../../providers.module/vertraege.service';
import { BuergscheineService } from '../../../../providers.module/buergscheine.service';
import { SortFunctions } from '../../../../pipes.module/_sort.functions';
import { Subscription } from 'rxjs';

@Component({
    selector: 'vertraege',
    templateUrl: './vertraege.component.html',
    styleUrls: ['./vertraege.component.less', '../../../commonless/content.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VertraegeComponent extends SuperComponent implements OnInit, OnDestroy {

    readonly buergscheinMetaData: TableCellDescriptor[] = BuergschaftenListComponent.buergscheineMetaDataTemplate;
    readonly buergscheinTargetParam = 'buergscheinId';

    private static readonly obligoPath: string = '/kreditportal/buergschaften/obligo';

    private readonly buergscheinTargetPath = '/kreditportal/buergschaften';

    private readonly filterCellNames: string[] = [
        'buergscheinId',
        'fassungsBetrag',
        'auftraggeberInfo',
        'ausstellungsDatum',
        'auftragInfo1'
    ];

    vertragName = signal<string>(null);
    currentOffset = signal(0);
    vertraegeList = signal<VertragData[]>([]);
    currVertrag = signal<VertragData>(null);
    visibleBuergscheine = signal<BuergscheinLight[]>([]);
    task = signal<string>(null);
    showAuslastungWarnung = signal(false);

    urlNeuenVertragAbschliessen = signal('');

    private currBuergscheine: BuergscheinLight[] = [];

    private subPage: Subscription;
    private queryParams: Subscription;

    constructor(
        private readonly appConstants: AppConstants,
        private readonly state: AppState,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly configurationApiService: ConfigurationApiService,
        private readonly vertraegeService: VertraegeService,
        private readonly buergscheineService: BuergscheineService
    ) {
        super();

        this.configurationApiService.getConfiguration().subscribe(configuration => {
            this.urlNeuenVertragAbschliessen.set(configuration.urlNeuenVertragAbschliessen);
        });

        this.vertraegeService.getVertrage().subscribe(vertraegeList => {
            this.vertraegeList.set(vertraegeList);
        });
    }

    ngOnInit() {
        this.subPage = this.activatedRoute.params.subscribe(params => {
            const tmpSubPage = params['subPage'];
            if (tmpSubPage) {
                this.state.registerForLastUrl();
                this.loadVertraege(tmpSubPage);
            } else {
                this.state.storeURL();
            }
        });

        this.queryParams = this.activatedRoute.queryParams.subscribe((params: Params) => {
            const task = params['task'];
            if (task) {
                this.task.set(task);
            } else {
                this.task.set(null);
            }
        });
    }

    ngOnDestroy() {
        this.currVertrag.set(null);
        this.vertraegeList.set([]);
        this.currBuergscheine = [];
        this.visibleBuergscheine.set([]);

        if (this.subPage) {
            this.subPage.unsubscribe();
        }

        if (this.queryParams) {
            this.queryParams.unsubscribe();
        }
    }

    goBack(): void {
        this.router.navigate([RouteNames.KREDIT_PORTAL_VERTRAEGE]);
    }

    private loadVertraege(vsnr: string): void {
        this.vertraegeService.getVertrag(vsnr).subscribe({
            next: (vertragData) => {
                this.currVertrag.set(vertragData);
                this.vertragName.set(vsnr);

                const showAuslastungWarnung = Math.round(vertragData.rahmenBelastung * 100 / vertragData.rahmenBetrag) >= this.appConstants.VERTRAG_AUSLASTUNG_WARNING_OVER;
                this.showAuslastungWarnung.set(showAuslastungWarnung);

                this.state.lastVertragDisplayed = vertragData.vertragHandle;

                this.buergscheineService.getBuergscheine(
                    vertragData.vertragHandle.vsnrNumerisch,
                    vertragData.geschaeftsjahr
                ).subscribe(buergscheinListe => {
                    if (!buergscheinListe || !buergscheinListe.buergscheine) {
                        this.currBuergscheine = [];
                        this.visibleBuergscheine.set([]);
                        return;
                    }

                    this.currBuergscheine = buergscheinListe.buergscheine;

                    const trimmedAndSortedBuergscheine = BuergschaftenListComponent.trimBuergscheine(
                        buergscheinListe.buergscheine
                    );
                    SortFunctions.sortArray4Buergscheine(trimmedAndSortedBuergscheine, 'buergscheinId', true);

                    this.visibleBuergscheine.set(trimmedAndSortedBuergscheine);
                });
            },
            error: () => {
                this.router.navigate(['./kreditportal/vertraege']);
            }
        });
    }

    vertragAendern(): void {
        this.router.navigate([], { queryParams: { task: 'erhoehen' } });
    }

    searchBuergschein = (term: string) => {
        const searchResults = FilterFunctions.filterMatrix(this.currBuergscheine, this.filterCellNames, term);
        this.visibleBuergscheine.set(BuergschaftenListComponent.trimBuergscheine(searchResults));
    }

    pagerAction = (page: number) => {
        this.currentOffset.set(page);
    }

    goToBuergscheinAction = (buerscheinNummer: string): void => {
        this.router.navigate(
            [this.buergscheinTargetPath, this.currVertrag().vertragHandle.vsnrNumerisch],
            { queryParams: { param: buerscheinNummer } },
        );
    }

    goToObligoAction = () => {
        this.router.navigate([VertraegeComponent.obligoPath]);
    }

}

